import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useClassNames, useMediaQuery } from '../../../../utils/hooks';
import { MainNavigationBlocks } from '../NavigationBlocks/NavigationBlocks';
import { navigationSectionStyles } from './navigationSection.styles';
import type { NavigationSectionClassNames, NavigationSectionProps, NavigationSectionStyles } from './types';

const sectionNested = 'section-nested';

export const NavigationSection = (props: NavigationSectionProps) => {
  const { isMobile } = useMediaQuery();
  const { config, setSoftExpand, setActiveNavigationKeys } = props;
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const classNames = useClassNames<NavigationSectionStyles, NavigationSectionClassNames>(
    'NavigationSection',
    navigationSectionStyles,
    undefined,
    !setSoftExpand && sectionNested
  );

  const noMobileHandlers = setSoftExpand
    ? {
        onMouseOver: (event: React.MouseEvent<HTMLElement>) => {
          const target = event.target as HTMLElement;

          if (target === event.currentTarget || !target.closest(`.${sectionNested}`)) {
            setSoftExpand(true);
          }
        },
        onMouseLeave: () => setSoftExpand(false),
      }
    : {};
  const mobileHandlers = setSoftExpand
    ? {
        onClick: (event: React.MouseEvent<HTMLElement>) => {
          const target = event.target as HTMLElement;

          if (target === event.currentTarget || !target.closest(`.${sectionNested}`)) {
            setSoftExpand(true);
          }
          if (target.closest('.element-active')) {
            setSoftExpand(false);
          }
        },
      }
    : {};
  const eventHandlers = isMobile ? mobileHandlers : noMobileHandlers;
  const navigationSections = config.section || {};

  const { pathname } = useLocation();
  if (`renderContent` in navigationSections && config.to && pathname.startsWith(config.to)) {
    // if the section is rendered via a custom renderer, we assume the entire section is active when the URL pathname
    // is prefixed by the element's "to" property - therefor all subsections must be a sub-path of the main section.
    // In that case, we just set the active key to this hardcoded value. The point is nothing is concerned with a value
    // of the navigation keys, it is only used to count the number of keys and if more than 1, the logic knows we have
    // a second level navigation, so we can set arbitrary array of length 2 or more, we are passing this way to at ease
    // debugging.
    // NOTE: this is called in a render function, which is an anti-pattern, but calling in in an useEffect does not work
    // for the other components call `setActiveNavigationKeys` during render as well and overwrites the value if called
    // in `useEffect`
    setActiveNavigationKeys([`section`, config.key]);
  }

  return (
    <div className={classNames.root} ref={setSoftExpand ? sectionRef : undefined} {...eventHandlers}>
      <div className={classNames.rootWrapper}>
        {`renderContent` in navigationSections ? (
          navigationSections.renderContent({ setActiveNavigationKeys })
        ) : (
          <>
            <div className={classNames.top}>
              <MainNavigationBlocks
                navBlocks={navigationSections.top || []}
                setActiveNavigationKeys={setActiveNavigationKeys}
              />
            </div>

            <div className={classNames.bottom}>
              <MainNavigationBlocks
                navBlocks={navigationSections.bottom || []}
                setActiveNavigationKeys={setActiveNavigationKeys}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
